import { T, useGT } from 'gt-react';
import { ChartPieSliceIcon } from '@phosphor-icons/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import {
  categoricalColor,
  reportNumberFormatOptions,
  type ReportColorPaletteColors,
} from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { defineComponentType } from '../registry';
import { useReportColors } from '../palette/ReportColorPaletteContext';
import { useReportDataset } from '../ReportDataContext';
import type { PieChartComponent } from '../types';
import {
  ColorSwatch,
  ColumnSelect,
  ConfigRow,
  DatasetSelectRow,
  ToggleRow,
  valueByCategoryTitle,
  ChartFrame,
  chartReady,
  useChartFit,
  ConfigSelectTrigger,
  columnLabelOf,
} from './shared';
import { NO_AXIS, PASTEL_PALETTE } from './chartConstants';
import {
  aggregateByCategory,
  NATURAL_ORDER,
  topWithOther,
  withNumericValues,
} from './chartRows';
import { BuiltinChart } from './BuiltinChart';
import type { BuiltinChartRequest } from './viz/request';

/** Max editable slice colors when "All" slices are shown. */
const PIE_COLOR_CAP = 20;

/**
 * Color for slice `index`: the stored custom color, else the palette's at the
 * slice's stable place (`colorIndexes`, see {@link stableSliceColorIndexes}).
 */
function pieColorAt(
  component: PieChartComponent,
  index: number,
  colors: ReportColorPaletteColors,
  colorIndexes: readonly number[],
): string {
  return (
    component.colors[index] ??
    categoricalColor(colors, colorIndexes[index] ?? index)
  );
}

/**
 * Each slice's place among every category of the dataset's rows before Report
 * filters, in natural order — the palette color it takes, so a category keeps
 * its color when a filter drops others. A slice no row names ("Other") comes
 * after them.
 */
export function stableSliceColorIndexes(
  slices: readonly string[],
  allNames: readonly string[],
): number[] {
  const domain = [...new Set(allNames)].sort((a, b) =>
    NATURAL_ORDER.compare(a, b),
  );
  const place = new Map(domain.map((name, index) => [name, index]));
  return slices.map((name) => place.get(name) ?? domain.length);
}

/**
 * Whether a pie's stored colors are the seed every pie got before palettes
 * (its slices then took these fixed pastels): such a pie follows the palette.
 */
export function hasSeededPieColors(colors: unknown): boolean {
  return (
    Array.isArray(colors) &&
    colors.length > 0 &&
    colors.every((color, index) => color === PASTEL_PALETTE[index])
  );
}

/**
 * Number of slices the chart will draw (= number of color swatches to show):
 * one per category, or top-N + an "Other" bucket. "All" (maxSlices 0) is capped at
 * PIE_COLOR_CAP. Falls back to the configured size before axes/data are set.
 */
function pieSliceCount(
  component: PieChartComponent,
  rowCount: number | null,
): number {
  if (
    rowCount === null ||
    component.nameKey === null ||
    component.valueKey === null
  ) {
    return component.maxSlices === 0 ? PIE_COLOR_CAP : component.maxSlices;
  }
  if (component.maxSlices === 0) return Math.min(rowCount, PIE_COLOR_CAP);
  if (rowCount <= component.maxSlices) return rowCount;
  return component.maxSlices + 1;
}

/** New colors array (length `count`) with `index` set to `next`. */
function setPieColorAt(
  component: PieChartComponent,
  index: number,
  next: string,
  count: number,
  palette: ReportColorPaletteColors,
  colorIndexes: readonly number[],
): string[] {
  const colors = Array.from({ length: count }, (_, i) =>
    pieColorAt(component, i, palette, colorIndexes),
  );
  colors[index] = next;
  return colors;
}

/** The slices a pie draws, and each one's stable palette place. */
function usePieSlices(component: PieChartComponent) {
  const gt = useGT();
  const dataset = useReportDataset(component.datasetKey);
  const ready = chartReady(dataset, component.nameKey, component.valueKey);
  const otherLabel = gt('Other');

  // Coerce values to numbers (the API may return numeric columns as
  // strings), then apply maxSlices: keep top-N by value, roll the rest into
  // "Other".
  const displayRows =
    ready && dataset
      ? topWithOther(
          // One slice per category: rows naming the same one sum into it.
          aggregateByCategory(
            withNumericValues(dataset.rows, component.valueKey ?? ''),
            component.nameKey,
            component.valueKey,
          ),
          component.nameKey ?? '',
          component.valueKey ?? '',
          component.maxSlices,
          otherLabel,
        )
      : [];

  const nameKey = component.nameKey;
  const allRows = dataset?.allRows ?? dataset?.rows;
  const colorIndexes = nameKey
    ? stableSliceColorIndexes(
        displayRows.map((row) => String(row[nameKey] ?? '')),
        (allRows ?? []).map((row) => String(row[nameKey] ?? '')),
      )
    : [];

  return { dataset, ready, displayRows, colorIndexes };
}

/** The tooltip writes a slice's value as a plain Number. */
const PLAIN_NUMBER = reportNumberFormatOptions(undefined);

function PieChartRenderer({ component }: { component: PieChartComponent }) {
  const { detail } = useChartFit();
  const palette = useReportColors();
  const { dataset, ready, displayRows, colorIndexes } = usePieSlices(component);
  const columns = dataset?.columns ?? [];
  const nameLabel = columnLabelOf(columns, component.nameKey);
  const valueLabel = columnLabelOf(columns, component.valueKey);
  const labels = component.showLabels && detail.tickLabels;
  const request: BuiltinChartRequest = {
    kind: 'pie',
    options: {
      data: displayRows,
      category: component.nameKey ?? '',
      value: component.valueKey ?? '',
      categoryTitle: nameLabel,
      valueTitle: valueLabel,
      donut: component.donut,
      labels,
      // Largest first (top N, then "Other"), clockwise from the top.
      keepOrder: true,
      sliceColors: displayRows.map((_, i) =>
        pieColorAt(component, i, palette, colorIndexes),
      ),
      valueFormat: PLAIN_NUMBER,
    },
  };

  return (
    <ChartFrame
      ready={ready}
      hasData={dataset !== null}
      icon={<ChartPieSliceIcon className="h-8 w-8 text-muted-foreground/40" />}
    >
      <BuiltinChart request={request} />
    </ChartFrame>
  );
}

function PieChartConfigForm({
  component,
  onUpdate,
}: {
  component: PieChartComponent;
  onUpdate: (updated: PieChartComponent) => void;
}) {
  const gt = useGT();
  const palette = useReportColors();
  const { dataset, ready, displayRows, colorIndexes } = usePieSlices(component);
  const allColumns = dataset?.columns ?? [];
  const numericColumns = allColumns.filter((c) => c.dataType === 'number');
  const sliceCount = pieSliceCount(
    component,
    ready ? displayRows.length : null,
  );

  return (
    <div className="space-y-4">
      <DatasetSelectRow
        component={component}
        onDatasetChange={(datasetKey) =>
          onUpdate({ ...component, datasetKey, nameKey: null, valueKey: null })
        }
      />
      <ConfigRow label={gt('Slice labels (category)')}>
        <ColumnSelect
          value={component.nameKey}
          columns={allColumns}
          placeholder={gt('Category column…')}
          onChange={(k) => onUpdate({ ...component, nameKey: k })}
        />
      </ConfigRow>
      <ConfigRow label={gt('Slice size (values)')}>
        <ColumnSelect
          value={component.valueKey}
          columns={
            numericColumns.length > 0
              ? numericColumns
              : [{ key: NO_AXIS, label: gt('No numeric columns in result') }]
          }
          placeholder={gt('Value column…')}
          onChange={(k) => onUpdate({ ...component, valueKey: k })}
        />
      </ConfigRow>
      <ConfigRow label={gt('Max slices')} htmlFor="pie-max-slices">
        <Select
          value={String(component.maxSlices)}
          onValueChange={(v) =>
            onUpdate({ ...component, maxSlices: Number(v) })
          }
        >
          <ConfigSelectTrigger id="pie-max-slices">
            <SelectValue />
          </ConfigSelectTrigger>
          <SelectContent>
            <SelectItem value="0">
              <T>All</T>
            </SelectItem>
            <SelectItem value="5">
              <T>Top 5</T>
            </SelectItem>
            <SelectItem value="8">
              <T>Top 8</T>
            </SelectItem>
            <SelectItem value="10">
              <T>Top 10</T>
            </SelectItem>
            <SelectItem value="15">
              <T>Top 15</T>
            </SelectItem>
            <SelectItem value="20">
              <T>Top 20</T>
            </SelectItem>
          </SelectContent>
        </Select>
      </ConfigRow>
      <ConfigRow label={gt('Slice colors')}>
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: sliceCount }, (_, i) => (
            <ColorSwatch
              key={i}
              value={pieColorAt(component, i, palette, colorIndexes)}
              label={gt('Slice color {number}', { number: i + 1 })}
              onChange={(next) =>
                onUpdate({
                  ...component,
                  colors: setPieColorAt(
                    component,
                    i,
                    next,
                    sliceCount,
                    palette,
                    colorIndexes,
                  ),
                })
              }
            />
          ))}
        </div>
        {component.colors.length > 0 && (
          <Button
            variant="link"
            size="sm"
            className="h-auto px-0 text-xs"
            onClick={() => onUpdate({ ...component, colors: [] })}
          >
            <T context="pie chart reset slice colors to the report palette">
              Use the report's palette
            </T>
          </Button>
        )}
      </ConfigRow>
      <ToggleRow
        label={gt('Donut style')}
        id="pie-donut"
        checked={component.donut}
        onCheckedChange={(v) => onUpdate({ ...component, donut: v })}
      />
      <ToggleRow
        label={gt('Show labels')}
        id="pie-labels"
        checked={component.showLabels}
        onCheckedChange={(v) => onUpdate({ ...component, showLabels: v })}
      />
    </div>
  );
}

export function PieChartPalettePreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <circle
          cx="22"
          cy="18"
          r="13"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <line
          x1="22"
          y1="18"
          x2="35"
          y2="18"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <line
          x1="22"
          y1="18"
          x2="12"
          y2="27"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <line
          x1="22"
          y1="18"
          x2="14"
          y2="7"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        {/* largest slice filled */}
        <path
          d="M 22 18 L 35 18 A 13 13 0 0 1 12 27 Z"
          fill="currentColor"
          fillOpacity="0.85"
        />
        <path
          d="M 22 18 L 12 27 A 13 13 0 0 1 14 7 Z"
          fill="currentColor"
          fillOpacity="0.35"
        />
        <path
          d="M 22 18 L 14 7 A 13 13 0 0 1 35 18 Z"
          fill="currentColor"
          fillOpacity="0.15"
        />
      </svg>
    </div>
  );
}

export const pieChartDefinition = defineComponentType<PieChartComponent>({
  type: 'pie-chart',
  label: 'Pie chart',
  defaultTitle: valueByCategoryTitle('nameKey', 'valueKey', 'Pie chart'),
  description:
    'Dataset-bound part-to-whole chart: slices named by the nameKey column, sized by the summed numeric valueKey column; donut=true for a donut, maxSlices rolls the tail into "Other".',
  columnRefFields: ['nameKey', 'valueKey'],
  version: 2,
  migrations: {
    // Pies once stored a fixed pastel seed; one still holding it follows the palette.
    1: (config) =>
      hasSeededPieColors(config.colors) ? { ...config, colors: [] } : config,
  },
  make: (id) => ({
    type: 'pie-chart',
    id,
    nameKey: null,
    valueKey: null,
    donut: false,
    showLabels: false,
    maxSlices: 8,
    colors: [],
  }),
  Renderer: PieChartRenderer,
  ConfigForm: PieChartConfigForm,
  Preview: PieChartPalettePreview,
});
